// One application: /pam/applications/:id — its settings (the edit form),
// who may use it, and how to connect it. Who may use it: Assignments for
// OpenID Connect and SAML apps, app policies for MFA API apps. There is no get-by-id call: the app is
// read from POST /api/v1/idp/apps/list.
import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal, Spin, Tabs, message } from "antd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";

import { PageHeader, PageShell, StatusPill } from "../../design-system/tableUi";
import { AN_COLORS, AN_MONO } from "../../design-system/tokens";
import { deleteApp, listApps } from "../../service/idpService";
import { card } from "../enduser/ssh-proxy/sshProxyUi";
import { ApplicationSettings } from "./ApplicationForm";
import AppAssignments from "./AppAssignments";
import AppPoliciesTab from "./AppPoliciesTab";
import HowToConnect from "./HowToConnect";
import { AppLogo, Forbidden, ProtocolBadge, reportIdpError } from "./appsUi";

const TABS = ["settings", "assignments", "connect"];
const MFA_API_TABS = ["settings", "policies", "connect"];

export default function ApplicationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [msg, msgCtx] = message.useMessage();
  const [modal, modalCtx] = Modal.useModal();

  const [app, setApp] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | missing | error | forbidden
  const [reloadKey, setReloadKey] = useState(0);

  const tabs = app?.protocol === "mfa_api" ? MFA_API_TABS : TABS;
  const tab = tabs.includes(params.get("tab")) ? params.get("tab") : "settings";
  const setTab = (t) => setParams(t === "settings" ? {} : { tab: t }, { replace: true });

  useEffect(() => {
    let alive = true;
    setState((s) => (s === "ready" ? s : "loading"));
    listApps()
      .then((res) => {
        if (!alive) return;
        const found = (res?.apps || []).find((a) => a.id === id);
        setApp(found || null);
        setState(found ? "ready" : "missing");
      })
      .catch((err) => {
        if (!alive) return;
        const f = reportIdpError(err, {
          msg,
          fallback: "Could not read the application.",
          setForbidden: () => setState("forbidden"),
        });
        if (!f.forbidden) setState("error");
      });
    return () => {
      alive = false;
    };
  }, [id, reloadKey, msg]);

  // A 404 from a call on this app: it is gone. Re-read, which shows "missing".
  const onNotFound = useCallback(() => setReloadKey((k) => k + 1), []);
  // After a secret rotation: read the app again for its new secret hint and
  // date. Rotating leaves updatedAt alone, so the settings form keeps its draft.
  const reload = onNotFound;

  const confirmDelete = () =>
    modal.confirm({
      title: `Delete ${app?.name || "this application"}?`,
      content: "Users will no longer be able to sign in to this app through AuthNull.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () =>
        deleteApp(app.id)
          .then(() => {
            msg.success("Application deleted.");
            navigate("/pam/applications", { replace: true });
          })
          .catch((err) =>
            reportIdpError(err, {
              msg,
              fallback: "Could not delete the application.",
              onNotFound: () => navigate("/pam/applications", { replace: true }),
            }),
          ),
    });

  const back = (
    <Button onClick={() => navigate("/pam/applications")} icon={<ArrowLeft size={14} style={{ verticalAlign: -2 }} />}>
      Applications
    </Button>
  );

  let body;
  if (state === "loading") {
    body = (
      <div style={{ padding: 60, textAlign: "center" }}>
        <Spin />
      </div>
    );
  } else if (state === "forbidden") {
    body = <Forbidden />;
  } else if (state === "missing" || state === "error") {
    body = (
      <div style={{ ...card, padding: 24, display: "grid", gap: 10, justifyItems: "start", maxWidth: "40rem" }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>
          {state === "missing" ? "No such application" : "Couldn't load the application"}
        </span>
        <span style={{ fontSize: 13, color: AN_COLORS.muted }}>
          {state === "missing" ? "It may have been deleted." : "Check your connection and try again."}
        </span>
        <span style={{ display: "inline-flex", gap: 8 }}>
          {state === "error" && <Button onClick={() => setReloadKey((k) => k + 1)}>Try again</Button>}
          <Button onClick={() => navigate("/pam/applications")}>Back to Applications</Button>
        </span>
      </div>
    );
  } else {
    body = (
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: "settings",
            label: "Settings",
            children: (
              <ApplicationSettings
                // A fresh form for each saved version.
                key={app.updatedAt || app.id}
                app={app}
                onSaved={(saved) => saved && setApp(saved)}
                onNotFound={onNotFound}
                onSecretRotated={reload}
              />
            ),
          },
          app.protocol === "mfa_api"
            ? {
                key: "policies",
                label: "Policies",
                children: <AppPoliciesTab app={app} active={tab === "policies"} />,
              }
            : { key: "assignments", label: "Assignments", children: <AppAssignments app={app} onNotFound={onNotFound} /> },
          {
            key: "connect",
            label: "How to connect",
            children: (
              <HowToConnect
                app={app}
                active={tab === "connect"}
                onSecretRotated={reload}
                onOpenPolicies={() => setTab("policies")}
                onOpenSettings={() => setTab("settings")}
              />
            ),
          },
        ]}
      />
    );
  }

  return (
    <PageShell>
      {msgCtx}
      {modalCtx}
      <PageHeader
        eyebrow="Applications"
        title={
          state === "ready" ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              <AppLogo app={app} size={30} />
              {app.name}
            </span>
          ) : (
            "Application"
          )
        }
        subtitle={
          state === "ready" && (app.oidc?.clientId || app.saml?.entityId) ? (
            <span style={{ fontFamily: AN_MONO, fontSize: 12.5 }}>{app.protocol === "saml" ? app.saml?.entityId : app.oidc?.clientId}</span>
          ) : undefined
        }
        chips={
          state === "ready" ? (
            <>
              <ProtocolBadge protocol={app.protocol} />
              <StatusPill tone={app.enabled ? "success" : "neutral"} label={app.enabled ? "Enabled" : "Disabled"} />
            </>
          ) : undefined
        }
        actions={
          <>
            {back}
            {state === "ready" && (
              <Button danger icon={<Trash2 size={14} style={{ verticalAlign: -2 }} />} onClick={confirmDelete}>
                Delete
              </Button>
            )}
          </>
        }
      />
      {body}
    </PageShell>
  );
}
